import React, { useRef, useState } from 'react';
import { Typography, Box, CircularProgress } from '@mui/material';
import DialogModal from '../../../components/DialogModal';
import ToastMessage from '../../../components/ToastMessage';
import { SnackbarSeverityType } from '../../../enums';
import {
  UploadButton,
  ControlsRow,
  CancelIcon,
  ChooseFileButton,
  ChooseFileButtonWrapper,
  UploadProgressWrapper,
  UploadProgress,
  HelperTextWrapper,
  SampleFileButton,
  SampleCSVIconWrapper
} from '../../ChequeStatusUpdate/styled';
import Icons from '../../../components/icons';
import { downloadCsvFromJson } from '../../../utils/fileGeneration';
import { getStaffAttendanceSample } from '../utils/staffAttendanceSample';
import staffAttendanceApis from '../../../apis/staffAttendance';
import { useSelector } from 'react-redux';
import { RootState } from '../../../redux/store';
import { Dayjs } from 'dayjs';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import CustomDatePicker from '../../../components/DatePicker';
import pallet from '../../../layouts/pallet';
import { fontSizes } from '../../../layouts/fontSizes';
import { handleStaffAttendanceUpload } from '../utils/staffAttendanceReportDownload';

interface UploadStaffAttendanceModalProps {
  open: boolean;
  onClose: () => void;
  date: Dayjs | null;
}

const UploadStaffAttendanceModal: React.FC<UploadStaffAttendanceModalProps> = ({
  open,
  onClose,
  date
}) => {
  const [fileName, setFileName] = useState<string>('');
  const [file, setFile] = useState<File | null>(null);
  const [selectedMonth, setSelectedMonth] = useState<Dayjs | null>(null);
  const [selectedYear, setSelectedYear] = useState<Dayjs | null>(null);
  const [uploadAttempted, setUploadAttempted] = useState<boolean>(false);
  const [uploading, setUploading] = useState<boolean>(false);
  const [uploadProgress, setUploadProgress] = useState<number>(0);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const [toastOpen, setToastOpen] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [toastSeverity, setToastSeverity] = useState<SnackbarSeverityType>(
    SnackbarSeverityType.success
  );

  const { user: loggedInUser } = useSelector(
    (state: RootState) => state.profile
  );

  const showToast = (message: string, severity: SnackbarSeverityType) => {
    setToastMessage(message);
    setToastSeverity(severity);
    setToastOpen(true);
  };
  const handleCloseToast = () => setToastOpen(false);

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files[0]) {
      setFileName(event.target.files[0].name);
      setFile(event.target.files[0]);
      setUploadAttempted(false);
    } else {
      setFileName('');
      setFile(null);
    }
  };

  const handleRemoveFile = () => {
    if (fileInputRef.current) fileInputRef.current.value = '';
    setFileName('');
    setFile(null);
    setUploadAttempted(false);
  };

  const handleUpload = async () => {
    setUploadAttempted(true);
    if (!file || !fileName) {
      showToast('Please select a file to upload', SnackbarSeverityType.error);
      return;
    }
    if (!selectedMonth || !selectedYear) {
      showToast('Please select Month and Year', SnackbarSeverityType.error);
      return;
    }
    try {
      setUploading(true);
      const month = selectedMonth.format('MMM').toLowerCase();
      const year = selectedYear.format('YYYY');
      const response = await staffAttendanceApis.importStaffAttendance({
        school: loggedInUser?.school ?? '',
        file,
        month,
        year
      });
      setUploadProgress(100);
      handleStaffAttendanceUpload({
        data: response || {},
        showToast,
        successMessage: 'File uploaded successfully.',
        unsuccessfulFileName: 'unsuccessful-staff-attendance-records',
        unsuccessfulSheetName: 'Unsuccessful Records'
      });
    } catch (e: any) {
      const message = e?.errors?.[0]?.message || 'Upload failed';
      showToast(message, SnackbarSeverityType.error);
    } finally {
      setUploading(false);
      setTimeout(() => setUploadProgress(0), 300);
    }
  };

  const handleChooseFileClick = () => {
    if (fileInputRef.current && !uploading) fileInputRef.current.click();
  };

  const showFieldError = uploadAttempted && !fileName;

  const handleDownloadSample = () => {
    const { headers, body } = getStaffAttendanceSample();
    downloadCsvFromJson('sample-staff-attendance', 'Sample', { headers, body });
  };

  return (
    <DialogModal
      open={open}
      onClose={onClose}
      title='Upload Staff Attendance'
      maxWidth='md'
    >
      <Box>
        <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
          <SampleFileButton
            onClick={handleDownloadSample}
            variant='outlined'
            color='primary'
          >
            <SampleCSVIconWrapper>
              <Icons.File />
            </SampleCSVIconWrapper>
            Download Sample CSV
          </SampleFileButton>
        </Box>
        <Box sx={{ display: 'flex', gap: 2, mb: 2, mt: 2, ml: 2 }}>
          <LocalizationProvider
            dateAdapter={AdapterDayjs}
            localeText={{ fieldMonthPlaceholder: () => 'MMM' }}
          >
            <CustomDatePicker
              label='Month'
              value={selectedMonth}
              onChange={setSelectedMonth}
              views={['month']}
              openTo='month'
              format='MMMM'
              width='100%'
              required
            />
            <CustomDatePicker
              label='Year'
              value={selectedYear}
              onChange={setSelectedYear}
              views={['year']}
              openTo='year'
              format='YYYY'
              width='100%'
              required
            />
          </LocalizationProvider>
        </Box>
        <Typography
          mt={2}
          mb='6px'
          ml={2}
          color={pallet.formLabelText}
          fontSize={`${fontSizes.placeHolder} !important`}
          fontWeight='600'
        >
          Browse Staff Attendance CSV{' '}
          <Box component='span' sx={{ color: 'red', display: 'inline' }}>
            *
          </Box>
        </Typography>
        <ControlsRow sx={{ ml: 2 }}>
          <ChooseFileButtonWrapper>
            <input
              type='file'
              ref={fileInputRef}
              onChange={handleFileChange}
              accept='.csv'
              style={{ display: 'none' }}
            />
            <ChooseFileButton
              isFileName={!!fileName}
              onClick={handleChooseFileClick}
              disabled={uploading}
            >
              {uploading && (
                <UploadProgressWrapper>
                  <UploadProgress
                    uploadProgress={uploadProgress}
                    sx={{ color: pallet.primary }}
                  />
                </UploadProgressWrapper>
              )}

              {!fileName && <Icons.UploadFile />}
              <Typography
                sx={{
                  fontSize: '16px',
                  whiteSpace: 'normal',
                  wordBreak: 'break-all',
                  textOverflow: 'unset',
                  overflow: 'visible'
                }}
                title={fileName || 'Choose File'}
              >
                {fileName || 'Choose File'}
              </Typography>
              {fileName && (
                <CancelIcon
                  onClick={(e) => {
                    e.stopPropagation();
                    handleRemoveFile();
                  }}
                  disabled={uploading}
                >
                  <Icons.Close />
                </CancelIcon>
              )}
            </ChooseFileButton>
            <HelperTextWrapper>Please upload CSV file only</HelperTextWrapper>
            {showFieldError && (
              <Typography color='error' variant='caption'>
                This field is required.
              </Typography>
            )}
          </ChooseFileButtonWrapper>

          <UploadButton
            variant='contained'
            color='primary'
            onClick={handleUpload}
            disabled={!file || !selectedMonth || !selectedYear}
          >
            {uploading ? <CircularProgress size={24} /> : 'Upload'}
          </UploadButton>
        </ControlsRow>
      </Box>
      <ToastMessage
        open={toastOpen}
        message={toastMessage}
        severity={toastSeverity}
        onClose={handleCloseToast}
      />
    </DialogModal>
  );
};

export default UploadStaffAttendanceModal;
